import React from "react";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { CompleteTaskEvent } from "../AmplitudeTaskManagementEvents";
import { AssignTaskTagEvent, RemoveTaskTagEvent } from "../AmplitudeTaskTagEvents";
import { ClientRecurringTask } from "../TaskManagementTypes";
import { useRecurringTaskMethods } from "../useRecurringTaskMethods";
import TaskDetails from "./TaskDetails";

type RecurringTaskDetailsProps = {
    task: ClientRecurringTask;
    amplitudeEventSource: AmplitudeEventSource;
};

export function RecurringTaskDetails(props: RecurringTaskDetailsProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const {
        overrideStatus,
        overrideTitle,
        overrideDescription,
        overrideTag,
        overrideProject,
        overrideTimeSpentMins,
        deleteOneInstance,
        overrideDuration,
        overrideDate,
        overrideTime,
        removeDuration,
        removeTime,
    } = useRecurringTaskMethods();

    return (
        <TaskDetails
            key={`task-details-${props.task.masterId}-${props.task.index}`}
            amplitudeEventSource={props.amplitudeEventSource}
            task={props.task}
            taskMethods={{
                buildKey: () => `${props.task.masterId}-${props.task.index}`,
                updateTaskStatus: (status) =>
                    overrideStatus({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        status,
                    }),
                updateTaskTitle: (title) =>
                    overrideTitle({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        title,
                    }),
                updateTaskDescription: (description) =>
                    overrideDescription({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        description,
                    }),
                updateTaskTag: (tagId) =>
                    overrideTag({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        tagId,
                    }),
                updateTaskProject: (projectId) =>
                    overrideProject({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        projectId,
                    }),
                updateTaskDuration: async (duration) => {
                    overrideDuration({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        durationMins: duration,
                    });
                },
                updateTaskDate: async (date) => {
                    overrideDate({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        date,
                    });
                },
                updateTaskTime: async (time) => {
                    overrideTime({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        time,
                    });
                },
                updateTaskTimeSpent: (timeSpent) =>
                    overrideTimeSpentMins({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        timeSpentMins: timeSpent,
                    }),
                removeTaskDuration: async () => {
                    removeDuration({
                        masterId: props.task.masterId,
                        index: props.task.index,
                    });
                },
                removeTaskTime: async () => {
                    removeTime({
                        masterId: props.task.masterId,
                        index: props.task.index,
                        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    });
                },
                removeTaskDate: null,
                makeTaskRecur: null,
                deleteTask: () =>
                    deleteOneInstance({
                        masterId: props.task.masterId,
                        index: props.task.index,
                    }),
                // TODO [LAS-1926]: Support due dates for recurring tasks
                removeTaskDueDate: null,
                updateTaskDueDate: null,
            }}
            events={{
                onComplete: () => {
                    amplitudeService.logEvent(
                        new CompleteTaskEvent({
                            type: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            source: props.amplitudeEventSource,
                        }),
                    );
                },
                onTagAssign: (tagId) => {
                    amplitudeService.logEvent(
                        new AssignTaskTagEvent({
                            taskType: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            tagId,
                            source: "task-details",
                        }),
                    );
                },
                onTagRemove: () => {
                    amplitudeService.logEvent(
                        new RemoveTaskTagEvent({
                            taskType: "recurring",
                            masterId: props.task.masterId,
                            index: props.task.index,
                            source: "task-details",
                        }),
                    );
                },
            }}
        />
    );
}
